import { useState, useMemo, useRef, useEffect, useCallback, lazy, Suspense } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Layout } from "@/components/layout/Layout";
import { Button, buttonVariants } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Textarea } from "@/components/ui/textarea";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/lib/authContext";
import {
  getFeedbackItems,
  updateFeedbackItem,
  deleteFeedbackItem,
  toggleFeedbackVote,
  getFeedbackAttachments,
  uploadFeedbackAttachment,
  deleteFeedbackAttachment,
  getFeedbackComments,
  postFeedbackComment,
  updateFeedbackComment,
  type FeedbackItem,
  type FeedbackAttachment,
  type FeedbackComment,
  type User,
  api,
} from "@/lib/api";
import { FeedbackSheet } from "@/components/custom/FeedbackSheet";
import { useFilterPresets, PRESETS_KEY_FEEDBACK } from "@/lib/filterPresets";
import { FilterPresetBar } from "@/components/custom/FilterPresetBar";
import {
  Lightbulb,
  Bug,
  Palette,
  Zap,
  Workflow,
  HelpCircle,
  Plus,
  Trash2,
  Pencil,
  ThumbsUp,
  MessageSquare,
  Paperclip,
  FileText,
  Image,
  ChevronDown,
  ChevronUp,
  Send,
  Check,
  X,
  Loader2,
  ScrollText,
  AtSign,
} from "lucide-react";
import { formatDistanceToNow } from "date-fns";

const ChangelogTab = lazy(() => import("@/components/custom/ChangelogTab"));

/** Render text with @mention highlighting */
function renderMentionBody(body: string) {
  const parts = body.split(/(@\w+)/g);
  return parts.map((part, i) =>
    /^@\w+$/.test(part)
      ? <span key={i} className="text-primary font-medium bg-primary/10 rounded px-0.5">{part}</span>
      : <span key={i}>{part}</span>
  );
}

// ---- Type metadata ----
const TYPE_CONFIG: Record<string, { label: string; icon: React.ElementType; color: string }> = {
  feature_request: { label: "Feature Request", icon: Lightbulb, color: "bg-blue-100 text-blue-800 border-blue-200" },
  bug:             { label: "Bug Report",       icon: Bug,       color: "bg-red-100 text-red-800 border-red-200" },
  ux:              { label: "UX / Workflow",    icon: Workflow,  color: "bg-purple-100 text-purple-800 border-purple-200" },
  look_and_feel:   { label: "Look & Feel",      icon: Palette,   color: "bg-amber-100 text-amber-800 border-amber-200" },
  performance:     { label: "Performance",      icon: Zap,       color: "bg-orange-100 text-orange-800 border-orange-200" },
  other:           { label: "Other",            icon: HelpCircle, color: "bg-slate-100 text-slate-700 border-slate-200" },
};

const STATUS_CONFIG: Record<string, { label: string; color: string }> = {
  new:         { label: "New",         color: "bg-slate-100 text-slate-700 border-slate-300" },
  reviewing:   { label: "Reviewing",   color: "bg-blue-100 text-blue-800 border-blue-200" },
  planned:     { label: "Planned",     color: "bg-violet-100 text-violet-800 border-violet-200" },
  in_progress: { label: "In Progress", color: "bg-amber-100 text-amber-800 border-amber-200" },
  done:        { label: "Done",        color: "bg-green-100 text-green-800 border-green-200" },
  wont_fix:    { label: "Won't Fix",   color: "bg-red-100 text-red-700 border-red-200" },
};

const PRIORITY_CONFIG: Record<string, { label: string; color: string }> = {
  low:      { label: "Low",      color: "bg-slate-100 text-slate-600 border-slate-200" },
  medium:   { label: "Medium",   color: "bg-blue-100 text-blue-700 border-blue-200" },
  high:     { label: "High",     color: "bg-orange-100 text-orange-800 border-orange-200" },
  critical: { label: "Critical", color: "bg-red-100 text-red-800 border-red-200" },
};

const FEEDBACK_TYPES = [
  { value: "feature_request", label: "Feature Request" },
  { value: "bug",             label: "Bug Report" },
  { value: "ux",              label: "UX / Workflow" },
  { value: "look_and_feel",   label: "Look & Feel" },
  { value: "performance",     label: "Performance" },
  { value: "other",           label: "Other" },
];

const STATUSES = [
  { value: "new",         label: "New" },
  { value: "reviewing",   label: "Reviewing" },
  { value: "planned",     label: "Planned" },
  { value: "in_progress", label: "In Progress" },
  { value: "done",        label: "Done" },
  { value: "wont_fix",    label: "Won't Fix" },
];

const PRIORITIES = [
  { value: "low",      label: "Low" },
  { value: "medium",   label: "Medium" },
  { value: "high",     label: "High" },
  { value: "critical", label: "Critical" },
];

function initials(name: string) {
  return name.split(" ").map((n) => n[0]).join("").toUpperCase().slice(0, 2);
}

function formatBytes(bytes: number | null) {
  if (!bytes) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function isImage(ct: string | null) { return ct?.startsWith("image/") ?? false; }

function TypeBadge({ type }: { type: string }) {
  const cfg = TYPE_CONFIG[type] ?? TYPE_CONFIG.other;
  const Icon = cfg.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border ${cfg.color}`}>
      <Icon className="h-3 w-3" />{cfg.label}
    </span>
  );
}

function StatusBadge({ status }: { status: string }) {
  const cfg = STATUS_CONFIG[status] ?? STATUS_CONFIG.new;
  return <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${cfg.color}`}>{cfg.label}</span>;
}

function PriorityBadge({ priority }: { priority: string }) {
  const cfg = PRIORITY_CONFIG[priority];
  if (!cfg) return null;
  return <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${cfg.color}`}>{cfg.label}</span>;
}

// ---- Attachments section ----
function AttachmentsSection({ feedbackId, currentUserId, isAdmin }: {
  feedbackId: string; currentUserId: string; isAdmin: boolean;
}) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { data: attachments = [], isLoading } = useQuery<FeedbackAttachment[]>({
    queryKey: ["feedback-attachments", feedbackId],
    queryFn: () => getFeedbackAttachments(feedbackId),
  });

  const uploadMutation = useMutation({
    mutationFn: (file: File) => uploadFeedbackAttachment(feedbackId, file),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["feedback-attachments", feedbackId] }),
    onError: (err: any) => toast({ title: "Upload failed", description: err.message, variant: "destructive" }),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => deleteFeedbackAttachment(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["feedback-attachments", feedbackId] }),
    onError: (err: any) => toast({ title: "Delete failed", description: err.message, variant: "destructive" }),
  });

  const handlePaste = (e: React.ClipboardEvent) => {
    const items = Array.from(e.clipboardData.items);
    const imageItems = items.filter((item) => item.kind === "file" && item.type.startsWith("image/"));
    imageItems.forEach((item) => {
      const file = item.getAsFile();
      if (file) uploadMutation.mutate(new File([file], `screenshot-${Date.now()}.png`, { type: file.type }));
    });
  };

  return (
    <div className="space-y-2" onPaste={handlePaste}>
      <div className="flex items-center justify-between">
        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
          Attachments {attachments.length > 0 && `(${attachments.length})`}
        </p>
        <button
          onClick={() => fileInputRef.current?.click()}
          disabled={uploadMutation.isPending}
          className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors"
        >
          {uploadMutation.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Paperclip className="h-3.5 w-3.5" />}
          {uploadMutation.isPending ? "Uploading…" : "Add File"}
        </button>
        <input ref={fileInputRef} type="file" multiple accept="image/*,.pdf,.doc,.docx,.txt,.csv,.xlsx"
          className="hidden" onChange={(e) => Array.from(e.target.files ?? []).forEach(f => uploadMutation.mutate(f))} />
      </div>

      {isLoading ? <p className="text-xs text-muted-foreground">Loading…</p>
        : attachments.length === 0 ? <p className="text-xs text-muted-foreground italic">No attachments — paste a screenshot or click "Add File".</p>
        : (
          <div className="space-y-2">
            {attachments.map((att) => (
              <div key={att.id} className="flex items-center gap-2 group">
                {isImage(att.content_type) && att.signed_url ? (
                  <a href={att.signed_url} target="_blank" rel="noopener noreferrer">
                    <img src={att.signed_url} alt={att.file_name}
                      className="h-16 w-24 object-cover rounded border hover:opacity-90 transition-opacity" />
                  </a>
                ) : (
                  <div className="h-10 w-10 rounded border bg-muted flex items-center justify-center shrink-0">
                    <FileText className="h-5 w-5 text-muted-foreground" />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  {att.signed_url
                    ? <a href={att.signed_url} target="_blank" rel="noopener noreferrer" className="text-xs font-medium hover:underline truncate block">{att.file_name}</a>
                    : <p className="text-xs font-medium truncate">{att.file_name}</p>}
                  <p className="text-[11px] text-muted-foreground">{formatBytes(att.file_size)}{att.uploader_name ? ` · ${att.uploader_name}` : ""}</p>
                </div>
                {(isAdmin || att.uploaded_by_user_id === currentUserId) && (
                  <button onClick={() => deleteMutation.mutate(att.id)}
                    className="opacity-40 group-hover:opacity-100 text-destructive hover:text-destructive/80 transition-all p-1">
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
    </div>
  );
}

// ---- Comments thread ----
function CommentsThread({ feedbackId, currentUserId }: { feedbackId: string; currentUserId: string }) {
  const { user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [newComment, setNewComment] = useState("");
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editBody, setEditBody] = useState("");
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [mentionQuery, setMentionQuery] = useState<string | null>(null);
  const [mentionIndex, setMentionIndex] = useState(0);

  const { data: staffUsers = [] } = useQuery<User[]>({
    queryKey: ["users-for-mentions"],
    queryFn: () => api.getUsers(),
    staleTime: 5 * 60 * 1000,
  });

  const mentionMatches = mentionQuery !== null
    ? staffUsers.filter(u =>
        u.name?.toLowerCase().startsWith(mentionQuery.toLowerCase()) ||
        (u.name?.split(" ") ?? []).some((w: string) => w.toLowerCase().startsWith(mentionQuery.toLowerCase()))
      ).slice(0, 6)
    : [];

  useEffect(() => { setMentionIndex(0); }, [mentionQuery]);

  const insertMention = useCallback((u: User) => {
    const ta = textareaRef.current;
    if (!ta) return;
    const cursor = ta.selectionStart ?? newComment.length;
    const before = newComment.slice(0, cursor);
    const after = newComment.slice(cursor);
    const handle = (u.name ?? "Unknown").replace(/\s+/g, "");
    const replaced = before.replace(/@\w*$/, `@${handle} `);
    setNewComment(replaced + after);
    setMentionQuery(null);
    requestAnimationFrame(() => {
      ta.focus();
      const pos = replaced.length;
      ta.setSelectionRange(pos, pos);
    });
  }, [newComment]);

  const { data: comments = [], isLoading } = useQuery<FeedbackComment[]>({
    queryKey: ["feedback-comments", feedbackId],
    queryFn: () => getFeedbackComments(feedbackId),
  });

  const postMutation = useMutation({
    mutationFn: () => postFeedbackComment(feedbackId, newComment),
    onSuccess: () => { setNewComment(""); setMentionQuery(null); queryClient.invalidateQueries({ queryKey: ["feedback-comments", feedbackId] }); },
    onError: (err: any) => toast({ title: "Failed to post comment", description: err.message, variant: "destructive" }),
  });

  const editMutation = useMutation({
    mutationFn: (id: string) => updateFeedbackComment(id, editBody),
    onSuccess: () => { setEditingId(null); queryClient.invalidateQueries({ queryKey: ["feedback-comments", feedbackId] }); },
    onError: (err: any) => toast({ title: "Failed to update comment", description: err.message, variant: "destructive" }),
  });

  return (
    <div className="space-y-3">
      <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
        Comments {comments.length > 0 && `(${comments.length})`}
      </p>

      {isLoading ? <p className="text-xs text-muted-foreground">Loading…</p>
        : comments.length === 0 ? <p className="text-xs text-muted-foreground italic">No comments yet.</p>
        : (
          <div className="space-y-3">
            {comments.map((c) => (
              <div key={c.id} className="flex gap-2.5 group">
                <Avatar className="h-6 w-6 shrink-0 mt-0.5">
                  <AvatarFallback className="text-[10px] bg-primary/10 text-primary">{initials(c.author_name)}</AvatarFallback>
                </Avatar>
                <div className="flex-1 min-w-0">
                  <div className="flex items-baseline gap-2 flex-wrap">
                    <span className="text-xs font-medium">{c.author_name}</span>
                    <span className="text-[11px] text-muted-foreground">{formatDistanceToNow(new Date(c.created_at), { addSuffix: true })}</span>
                    {c.author_user_id === currentUserId && editingId !== c.id && (
                      <button onClick={() => { setEditingId(c.id); setEditBody(c.body); }}
                        className="opacity-40 group-hover:opacity-100 text-muted-foreground hover:text-foreground transition-all ml-1">
                        <Pencil className="h-3 w-3" />
                      </button>
                    )}
                  </div>
                  {editingId === c.id ? (
                    <div className="mt-1 space-y-1.5">
                      <Textarea value={editBody} onChange={(e) => setEditBody(e.target.value)}
                        rows={2} className="text-sm" autoFocus />
                      <div className="flex gap-1.5">
                        <Button size="sm" className="h-6 text-xs px-2"
                          onClick={() => editMutation.mutate(c.id)}
                          disabled={!editBody.trim() || editMutation.isPending}>
                          {editMutation.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : <Check className="h-3 w-3" />}
                        </Button>
                        <Button size="sm" variant="outline" className="h-6 text-xs px-2" onClick={() => setEditingId(null)}>
                          <X className="h-3 w-3" />
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <p className="text-sm mt-0.5 whitespace-pre-wrap">{renderMentionBody(c.body)}</p>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

      {/* Compose */}
      <div className="flex gap-2 pt-1">
        <Avatar className="h-6 w-6 shrink-0 mt-1">
          <AvatarFallback className="text-[10px] bg-primary/10 text-primary">
            {user?.name ? initials(user.name) : "?"}
          </AvatarFallback>
        </Avatar>
        <div className="flex-1 space-y-1.5">
          {/* @mention autocomplete */}
          {mentionQuery !== null && mentionMatches.length > 0 && (
            <div className="border rounded-md bg-popover shadow-md overflow-hidden">
              {mentionMatches.map((u, i) => (
                <button
                  key={u.id}
                  className={`w-full flex items-center gap-2 px-3 py-1.5 text-xs text-left hover:bg-muted/50 transition-colors ${i === mentionIndex ? "bg-muted" : ""}`}
                  onMouseDown={e => { e.preventDefault(); insertMention(u); }}
                  onMouseEnter={() => setMentionIndex(i)}
                >
                  <span className="w-5 h-5 rounded-full bg-primary/20 text-primary text-[9px] font-bold flex items-center justify-center shrink-0">
                    {(u.name?.[0] ?? "?").toUpperCase()}
                  </span>
                  {u.name}
                </button>
              ))}
            </div>
          )}
          <div className="flex gap-2">
            <textarea
              ref={textareaRef}
              placeholder="Add a comment… Type @ to mention someone."
              rows={2}
              value={newComment}
              onChange={(e) => {
                setNewComment(e.target.value);
                const cursor = e.target.selectionStart ?? e.target.value.length;
                const toCursor = e.target.value.slice(0, cursor);
                const match = toCursor.match(/@(\w*)$/);
                setMentionQuery(match ? match[1] : null);
              }}
              onKeyDown={(e) => {
                if (mentionQuery !== null && mentionMatches.length > 0) {
                  if (e.key === "ArrowDown") { e.preventDefault(); setMentionIndex(i => Math.min(i + 1, mentionMatches.length - 1)); return; }
                  if (e.key === "ArrowUp") { e.preventDefault(); setMentionIndex(i => Math.max(i - 1, 0)); return; }
                  if (e.key === "Enter" || e.key === "Tab") { e.preventDefault(); insertMention(mentionMatches[mentionIndex]); return; }
                  if (e.key === "Escape") { setMentionQuery(null); return; }
                }
                if (e.key === "Enter" && (e.metaKey || e.ctrlKey) && newComment.trim()) postMutation.mutate();
              }}
              className="flex-1 resize-none text-sm rounded-md border border-input bg-background px-3 py-2 placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
            />
            <Button size="icon" className="h-8 w-8 shrink-0 self-end"
              disabled={!newComment.trim() || postMutation.isPending} onClick={() => postMutation.mutate()}>
              {postMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ---- Feedback card ----
function FeedbackCard({ item, currentUserId, isAdminOrManager }: {
  item: FeedbackItem; currentUserId: string; isAdminOrManager: boolean;
}) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [expanded, setExpanded] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editTitle, setEditTitle] = useState(item.title);
  const [editDesc, setEditDesc] = useState(item.description ?? "");
  const [editType, setEditType] = useState<string>(item.type);
  const [editingNotes, setEditingNotes] = useState(false);
  const [notesValue, setNotesValue] = useState(item.admin_notes ?? "");

  const isOwner = item.submitter_user_id === currentUserId;
  const canEdit = isAdminOrManager || (isOwner && item.status === "new");
  const canDelete = isAdminOrManager || (isOwner && item.status === "new");

  const [voteCount, setVoteCount] = useState(item.vote_count);
  const [hasVoted, setHasVoted] = useState(item.has_voted);

  const voteMutation = useMutation({
    mutationFn: () => toggleFeedbackVote(item.id),
    onMutate: () => { setHasVoted((v) => !v); setVoteCount((c) => hasVoted ? c - 1 : c + 1); },
    onSuccess: (data) => { setHasVoted(data.voted); setVoteCount(data.vote_count); },
    onError: () => { setHasVoted(item.has_voted); setVoteCount(item.vote_count); },
  });

  const updateMutation = useMutation({
    mutationFn: (data: Parameters<typeof updateFeedbackItem>[1]) => updateFeedbackItem(item.id, data),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ["feedback"] }); setEditing(false); },
    onError: (err: any) => toast({ title: "Update failed", description: err.message, variant: "destructive" }),
  });

  const deleteMutation = useMutation({
    mutationFn: () => deleteFeedbackItem(item.id),
    onSuccess: () => { toast({ title: "Feedback deleted" }); queryClient.invalidateQueries({ queryKey: ["feedback"] }); },
    onError: (err: any) => toast({ title: "Delete failed", description: err.message, variant: "destructive" }),
  });

  const timeAgo = formatDistanceToNow(new Date(item.created_at), { addSuffix: true });

  return (
    <div className="border rounded-lg bg-card overflow-hidden">
      <div className="p-4 space-y-2.5">
        {/* Badges + meta */}
        <div className="flex items-center gap-2 flex-wrap">
          <TypeBadge type={item.type} />
          <StatusBadge status={item.status} />
          {item.priority && <PriorityBadge priority={item.priority} />}
          <span className="ml-auto text-xs text-muted-foreground whitespace-nowrap">
            {item.submitter_name ?? "Unknown"} · {timeAgo}
          </span>
        </div>

        {/* Title row */}
        {editing ? (
          <div className="space-y-2">
            <Select value={editType} onValueChange={setEditType}>
              <SelectTrigger className="h-8 text-sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {FEEDBACK_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
              </SelectContent>
            </Select>
            <Input value={editTitle} onChange={(e) => setEditTitle(e.target.value)} placeholder="Title" className="text-sm" />
            <Textarea value={editDesc} onChange={(e) => setEditDesc(e.target.value)}
              placeholder="Details (optional)" rows={3} className="text-sm" />
            <div className="flex gap-2">
              <Button size="sm" className="h-7 text-xs"
                disabled={!editTitle.trim() || updateMutation.isPending}
                onClick={() => updateMutation.mutate({ title: editTitle, description: editDesc || null, type: editType })}>
                {updateMutation.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : null}
                Save
              </Button>
              <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => { setEditing(false); setEditTitle(item.title); setEditDesc(item.description ?? ""); setEditType(item.type); }}>
                Cancel
              </Button>
            </div>
          </div>
        ) : (
          <>
            <div className="flex items-start justify-between gap-2">
              <p className="font-medium text-sm leading-snug">{item.title}</p>
              <div className="flex items-center gap-1 shrink-0">
                {/* Upvote */}
                <button onClick={() => voteMutation.mutate()} disabled={voteMutation.isPending}
                  className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium border transition-colors ${
                    hasVoted ? "bg-primary/10 text-primary border-primary/30 hover:bg-primary/20"
                    : "bg-transparent text-muted-foreground border-border hover:bg-accent hover:text-accent-foreground"
                  }`} title={hasVoted ? "Remove upvote" : "Upvote"}>
                  <ThumbsUp className={`h-3.5 w-3.5 ${hasVoted ? "fill-primary" : ""}`} />
                  {voteCount > 0 && <span>{voteCount}</span>}
                </button>
                {canEdit && (
                  <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setEditing(true)}>
                    <Pencil className="h-3.5 w-3.5" />
                  </Button>
                )}
                {canDelete && (
                  <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive hover:text-destructive"
                    onClick={() => setDeleteOpen(true)}>
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                )}
              </div>
            </div>

            {/* Description always visible */}
            {item.description && (
              <p className="text-sm text-muted-foreground whitespace-pre-wrap leading-relaxed">{item.description}</p>
            )}
          </>
        )}
      </div>

      {/* Expand bar — prominent, always at the bottom of the card header */}
      {!editing && (
        <button
          onClick={() => setExpanded((v) => !v)}
          className="w-full flex items-center justify-between px-4 py-2.5 bg-muted/40 hover:bg-muted/70 transition-colors border-t text-xs text-muted-foreground hover:text-foreground"
        >
          <div className="flex items-center gap-3">
            <span className="flex items-center gap-1">
              <Paperclip className="h-3.5 w-3.5" />
              Attachments
            </span>
            <span className="flex items-center gap-1">
              <MessageSquare className="h-3.5 w-3.5" />
              Comments
            </span>
            {isAdminOrManager && (
              <span className="text-muted-foreground/60">· Admin Triage</span>
            )}
          </div>
          {expanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
        </button>
      )}

      {/* Expanded content */}
      {expanded && !editing && (
        <div className="border-t bg-muted/20 p-4 space-y-5">
          <AttachmentsSection feedbackId={item.id} currentUserId={currentUserId} isAdmin={isAdminOrManager} />
          <div className="border-t" />
          <CommentsThread feedbackId={item.id} currentUserId={currentUserId} />

          {/* Admin triage */}
          {isAdminOrManager && (
            <div className="space-y-3 pt-1 border-t">
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide pt-1">Admin Triage</p>
              <div className="flex flex-wrap gap-3">
                <div className="space-y-1">
                  <p className="text-xs text-muted-foreground">Type</p>
                  <Select value={item.type} onValueChange={(val) => updateMutation.mutate({ type: val })}>
                    <SelectTrigger className="h-7 text-xs w-36"><SelectValue /></SelectTrigger>
                    <SelectContent>{FEEDBACK_TYPES.map((t) => <SelectItem key={t.value} value={t.value} className="text-xs">{t.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <p className="text-xs text-muted-foreground">Status</p>
                  <Select value={item.status} onValueChange={(val) => updateMutation.mutate({ status: val })}>
                    <SelectTrigger className="h-7 text-xs w-32"><SelectValue /></SelectTrigger>
                    <SelectContent>{STATUSES.map((s) => <SelectItem key={s.value} value={s.value} className="text-xs">{s.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <p className="text-xs text-muted-foreground">Priority</p>
                  <Select value={item.priority ?? "none"} onValueChange={(val) => updateMutation.mutate({ priority: val === "none" ? null : val })}>
                    <SelectTrigger className="h-7 text-xs w-28"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none" className="text-xs text-muted-foreground">— None —</SelectItem>
                      {PRIORITIES.map((p) => <SelectItem key={p.value} value={p.value} className="text-xs">{p.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                {item.status !== "done" ? (
                  <Button size="sm" className="h-7 text-xs bg-green-600 hover:bg-green-700 text-white"
                    onClick={() => updateMutation.mutate({ status: "done" })}>
                    ✓ Mark Done
                  </Button>
                ) : (
                  <Button size="sm" variant="outline" className="h-7 text-xs"
                    onClick={() => updateMutation.mutate({ status: "new" })}>
                    Reopen
                  </Button>
                )}
              </div>
              <div className="space-y-1">
                <div className="flex items-center justify-between">
                  <p className="text-xs text-muted-foreground">Admin Notes</p>
                  {!editingNotes && (
                    <Button variant="ghost" size="sm" className="h-6 text-xs"
                      onClick={() => { setNotesValue(item.admin_notes ?? ""); setEditingNotes(true); }}>
                      {item.admin_notes ? "Edit" : "Add Note"}
                    </Button>
                  )}
                </div>
                {editingNotes ? (
                  <div className="space-y-2">
                    <Textarea value={notesValue} onChange={(e) => setNotesValue(e.target.value)} rows={2} className="text-xs" />
                    <div className="flex gap-2">
                      <Button size="sm" className="h-7 text-xs" onClick={() => { updateMutation.mutate({ admin_notes: notesValue || null }); setEditingNotes(false); }}>Save</Button>
                      <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setEditingNotes(false)}>Cancel</Button>
                    </div>
                  </div>
                ) : item.admin_notes ? (
                  <p className="text-xs text-muted-foreground bg-muted/50 rounded px-2 py-1.5 whitespace-pre-wrap">{item.admin_notes}</p>
                ) : null}
              </div>
            </div>
          )}
        </div>
      )}

      <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this feedback?</AlertDialogTitle>
            <AlertDialogDescription>This cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction className={buttonVariants({ variant: "destructive" })} onClick={() => deleteMutation.mutate()}>Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

interface FeedbackFilters {
  typeFilter: string;
  statusFilter: string;
  mineOnly: boolean;
  openOnly: boolean;
}

const OPEN_STATUSES = new Set(["new", "reviewing", "planned", "in_progress"]);

// ---- Main page ----
export default function FeedbackPage() {
  const { user } = useAuth();
  const [submitOpen, setSubmitOpen] = useState(false);
  const [typeFilter, setTypeFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [mineOnly, setMineOnly] = useState(false);
  const [openOnly, setOpenOnly] = useState(true);

  // Deep link from notification emails: /feedback?item=<id> scrolls to and
  // highlights that card. Resolved items are hidden by the default Open
  // filter, so a deep link also widens the filter.
  const [highlightItemId] = useState<string | null>(
    () => new URLSearchParams(window.location.search).get("item")
  );
  useEffect(() => {
    if (highlightItemId) setOpenOnly(false);
  }, [highlightItemId]);

  const isAdminOrManager = user ? ["admin", "manager"].includes(user.role) : false;

  // Separate query for status counts — always unfiltered so counts are stable regardless of active filters
  const { data: allItemsForCounts = [] } = useQuery({
    queryKey: ["feedback-counts"],
    queryFn: () => getFeedbackItems({}),
    staleTime: 60_000,
  });
  const statusCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const item of allItemsForCounts) {
      counts[item.status] = (counts[item.status] ?? 0) + 1;
    }
    return counts;
  }, [allItemsForCounts]);
  const openCount = (statusCounts["new"] ?? 0) + (statusCounts["reviewing"] ?? 0) + (statusCounts["planned"] ?? 0) + (statusCounts["in_progress"] ?? 0);
  const doneCount = (statusCounts["done"] ?? 0) + (statusCounts["wont_fix"] ?? 0);

  // When openOnly is active, fetch all statuses (we filter client-side to exclude done/wont_fix)
  const { data: rawItems = [], isLoading } = useQuery({
    queryKey: ["feedback", typeFilter, openOnly ? "open" : statusFilter, mineOnly],
    queryFn: () => getFeedbackItems({
      type: typeFilter !== "all" ? typeFilter : undefined,
      status: !openOnly && statusFilter !== "all" ? statusFilter : undefined,
      mine: mineOnly,
    }),
  });

  const items = openOnly
    ? rawItems.filter((item) => OPEN_STATUSES.has(item.status))
    : rawItems;

  useEffect(() => {
    if (!highlightItemId || isLoading) return;
    const el = document.getElementById(`feedback-item-${highlightItemId}`);
    if (!el) return;
    el.scrollIntoView({ behavior: "smooth", block: "center" });
    el.classList.add("ring-2", "ring-emerald-400", "rounded-lg");
    const t = setTimeout(() => el.classList.remove("ring-2", "ring-emerald-400"), 4000);
    return () => clearTimeout(t);
  }, [highlightItemId, isLoading, items.length]);

  // Filter presets
  const { presets: filterPresets, save: savePreset, remove: removePreset } = useFilterPresets<FeedbackFilters>(PRESETS_KEY_FEEDBACK);
  const currentFilters = useMemo<FeedbackFilters>(() => ({
    typeFilter, statusFilter, mineOnly, openOnly,
  }), [typeFilter, statusFilter, mineOnly, openOnly]);
  function applyFilters(f: FeedbackFilters) {
    setTypeFilter(f.typeFilter);
    setStatusFilter(f.statusFilter);
    setMineOnly(f.mineOnly);
    setOpenOnly(f.openOnly);
  }
  const hasActiveFilters = typeFilter !== "all" || statusFilter !== "all" || mineOnly || openOnly;

  return (
    <Layout>
      <div className="p-6 max-w-3xl mx-auto space-y-6">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-semibold">Feedback & Ideas</h1>
            <p className="text-sm text-muted-foreground mt-0.5">Submit bugs, feature requests, and suggestions for CentCom</p>
            {allItemsForCounts.length > 0 && (
              <div className="flex items-center gap-1.5 mt-2 flex-wrap">
                <span className="text-xs text-muted-foreground">{allItemsForCounts.length} total ·</span>
                {openCount > 0 && (
                  <button onClick={() => { setOpenOnly(true); setStatusFilter("all"); }}
                    className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 hover:bg-amber-200 transition-colors text-xs font-medium">
                    <span className="w-1.5 h-1.5 rounded-full bg-amber-500 inline-block" />{openCount} open
                  </button>
                )}
                {(statusCounts["planned"] ?? 0) > 0 && (
                  <button onClick={() => { setOpenOnly(false); setStatusFilter("planned"); }}
                    className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-blue-100 text-blue-800 hover:bg-blue-200 transition-colors text-xs font-medium">
                    <span className="w-1.5 h-1.5 rounded-full bg-blue-500 inline-block" />{statusCounts["planned"]} planned
                  </button>
                )}
                {doneCount > 0 && (
                  <button onClick={() => { setOpenOnly(false); setStatusFilter("done"); }}
                    className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-green-100 text-green-800 hover:bg-green-200 transition-colors text-xs font-medium">
                    <span className="w-1.5 h-1.5 rounded-full bg-green-500 inline-block" />{doneCount} closed
                  </button>
                )}
              </div>
            )}
          </div>
          <Button onClick={() => setSubmitOpen(true)} className="shrink-0">
            <Plus className="h-4 w-4 mr-2" />Submit Feedback
          </Button>
        </div>

        <Tabs defaultValue="feedback">
          <TabsList className="mb-4">
            <TabsTrigger value="feedback" className="gap-1.5">
              <Lightbulb className="h-3.5 w-3.5" />Feedback
            </TabsTrigger>
            <TabsTrigger value="changelog" className="gap-1.5">
              <ScrollText className="h-3.5 w-3.5" />Changelog
            </TabsTrigger>
          </TabsList>

          <TabsContent value="feedback" className="space-y-4">
            <div className="flex items-center gap-2 flex-wrap">
              <Select value={typeFilter} onValueChange={setTypeFilter}>
                <SelectTrigger className="h-8 text-sm w-40"><SelectValue placeholder="All Types" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  {FEEDBACK_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); if (v !== "all") setOpenOnly(false); }}>
                <SelectTrigger className="h-8 text-sm w-36"><SelectValue placeholder="All Statuses" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  {STATUSES.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                </SelectContent>
              </Select>
              <Button
                variant={openOnly ? "default" : "outline"}
                size="sm"
                className="h-8"
                onClick={() => { setOpenOnly((v) => !v); if (!openOnly) setStatusFilter("all"); }}
              >
                Open Items
              </Button>
              <Button variant={mineOnly ? "default" : "outline"} size="sm" className="h-8" onClick={() => setMineOnly((v) => !v)}>
                Mine Only
              </Button>
              <FilterPresetBar
                presets={filterPresets}
                currentFilters={currentFilters}
                hasActiveFilters={hasActiveFilters}
                onApply={applyFilters}
                onSave={savePreset}
                onRemove={removePreset}
              />
              {hasActiveFilters && (
                <Button variant="ghost" size="sm" className="h-8"
                  onClick={() => { setTypeFilter("all"); setStatusFilter("all"); setMineOnly(false); setOpenOnly(false); }}>
                  Clear
                </Button>
              )}
            </div>

            {isLoading ? (
              <div className="text-center text-muted-foreground py-12 text-sm">Loading…</div>
            ) : items.length === 0 ? (
              <div className="text-center py-16 space-y-3">
                <Lightbulb className="h-10 w-10 text-muted-foreground/40 mx-auto" />
                <p className="text-muted-foreground text-sm">No feedback yet — be the first to submit an idea!</p>
                <Button variant="outline" onClick={() => setSubmitOpen(true)}>
                  <Plus className="h-4 w-4 mr-2" />Submit Feedback
                </Button>
              </div>
            ) : (
              <div className="space-y-3">
                {items.map((item) => (
                  <div key={item.id} id={`feedback-item-${item.id}`}>
                    <FeedbackCard item={item} currentUserId={user?.id ?? ""} isAdminOrManager={isAdminOrManager} />
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="changelog" className="space-y-6">
            <Suspense fallback={<div className="py-8 text-sm text-muted-foreground">Loading changelog…</div>}>
              <ChangelogTab />
            </Suspense>
          </TabsContent>
        </Tabs>
      </div>

      <FeedbackSheet open={submitOpen} onOpenChange={setSubmitOpen} />
    </Layout>
  );
}
